import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Calendar, CheckCircle2, Clock, Filter, Wrench, Briefcase, Heart, Music, CheckSquare, Trash2, Edit, Repeat, Plane, PartyPopper, GraduationCap, Stethoscope } from 'lucide-react';
import { format, isPast, isToday } from 'date-fns';
import { es } from 'date-fns/locale';
import { RecurringTaskDialog } from './components/RecurringTaskDialog';

const FREQUENCY_LABELS = {
  daily: 'Diaria',
  weekly: 'Semanal',
  biweekly: 'Quincenal',
  monthly: 'Mensual',
  yearly: 'Anual'
};

const TASK_ICONS = {
  montaje: Wrench,
  cita_profesional: Briefcase,
  cita_personal: Heart,
  ensayo: Music,
  tarea_general: CheckSquare,
  viaje: Plane,
  evento_personal: PartyPopper,
  formacion: GraduationCap,
  medico: Stethoscope
};

const TASK_COLORS = {
  montaje: '#f59e0b',
  cita_profesional: '#3b82f6',
  cita_personal: '#ec4899',
  ensayo: '#8b5cf6',
  tarea_general: '#10b981',
  viaje: '#06b6d4',
  evento_personal: '#f472b6',
  formacion: '#a78bfa',
  medico: '#ef4444'
};

const TASK_TYPE_LABELS = {
  montaje: 'Montaje',
  cita_profesional: 'Cita Profesional',
  cita_personal: 'Cita Personal',
  ensayo: 'Ensayo',
  tarea_general: 'Tarea General',
  viaje: 'Viaje',
  evento_personal: 'Evento Personal',
  formacion: 'Formación',
  medico: 'Cita Médica'
};

const PRIORITY_LABELS = {
  low: '🟢 Baja',
  medium: '🟡 Media',
  high: '🟠 Alta',
  urgent: '🔴 Urgente'
};

const STATUS_LABELS = {
  pending: 'Pendiente',
  completed: 'Completada',
  cancelled: 'Cancelada'
};

export const TasksView = ({ tasks, onEditTask, onCompleteTask, onDeleteTask, onDeleteTaskSeries, onEditTaskSeries }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [showPast, setShowPast] = useState(false);
  
  // Estado para el diálogo de tareas recurrentes
  const [recurringDialogOpen, setRecurringDialogOpen] = useState(false);
  const [recurringDialogAction, setRecurringDialogAction] = useState('delete'); // 'delete' | 'edit'
  const [selectedTask, setSelectedTask] = useState(null);

  // Handlers para tareas recurrentes
  const handleDeleteClick = (task) => {
    const isRecurring = task.is_recurring || task.parent_task_id;
    if (isRecurring) {
      setSelectedTask(task);
      setRecurringDialogAction('delete');
      setRecurringDialogOpen(true);
    } else {
      if (window.confirm('¿Eliminar esta tarea?')) {
        onDeleteTask(task.id);
      }
    }
  };

  const handleEditClick = (task) => {
    const isRecurring = task.is_recurring || task.parent_task_id;
    if (isRecurring) {
      setSelectedTask(task);
      setRecurringDialogAction('edit');
      setRecurringDialogOpen(true);
    } else {
      onEditTask(task);
    }
  };

  const handleRecurringConfirm = async (mode) => {
    if (recurringDialogAction === 'delete') {
      await onDeleteTaskSeries(selectedTask.id, mode);
    } else {
      // Para editar, pasar el modo al componente padre que abrirá el form
      onEditTaskSeries(selectedTask, mode);
    }
    setRecurringDialogOpen(false);
    setSelectedTask(null);
  };

  // Filtrar y ordenar tareas
  const filteredAndSortedTasks = useMemo(() => {
    let filtered = tasks;

    // Ocultar tareas pasadas por defecto (antes de hoy) - SIN IMPORTAR EL ESTADO
    if (!showPast) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      filtered = filtered.filter(task => {
        // Usar start_datetime o due_date, lo que esté disponible
        const taskDateStr = task.start_datetime || task.due_date;
        if (!taskDateStr) return true; // Sin fecha = mostrar siempre
        
        const taskDate = new Date(taskDateStr);
        taskDate.setHours(0, 0, 0, 0);
        // Solo mostrar si fecha >= hoy
        return taskDate >= today;
      });
    }

    // Filtro de búsqueda
    if (searchTerm) {
      filtered = filtered.filter(task =>
        task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (task.notes && task.notes.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Filtro por tipo
    if (filterType !== 'all') {
      filtered = filtered.filter(task => task.task_type === filterType);
    }

    // Filtro por estado
    if (filterStatus !== 'all') {
      filtered = filtered.filter(task => task.status === filterStatus);
    }

    // Filtro por prioridad
    if (filterPriority !== 'all') {
      filtered = filtered.filter(task => task.priority === filterPriority);
    }

    // Ordenar
    const sorted = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'date':
          // Ordenar por fecha y hora (más próximos primero)
          const dateA = new Date(a.start_datetime);
          const dateB = new Date(b.start_datetime);
          const dateDiff = dateA - dateB;
          
          // Si las fechas son diferentes, ordenar por fecha
          if (dateDiff !== 0) return dateDiff;
          
          // Si las fechas son iguales, ordenar por hora numéricamente
          const timeA = a.horario_inicio || a.start_time || '';
          const timeB = b.horario_inicio || b.start_time || '';
          
          if (timeA && timeB) {
            // Convertir hora "HH:MM" a minutos para comparación numérica
            const parseTime = (timeStr) => {
              const [hours, minutes] = timeStr.split(':').map(Number);
              return hours * 60 + minutes;
            };
            
            return parseTime(timeA) - parseTime(timeB);
          }
          
          // Si solo uno tiene hora, ponerlo primero
          if (timeA && !timeB) return -1;
          if (!timeA && timeB) return 1;
          
          return 0;
        case 'date_desc':
          // Ordenar por fecha y hora (más lejanos primero)
          const dateDescA = new Date(b.start_datetime);
          const dateDescB = new Date(a.start_datetime);
          const dateDescDiff = dateDescA - dateDescB;
          
          // Si las fechas son diferentes, ordenar por fecha
          if (dateDescDiff !== 0) return dateDescDiff;
          
          // Si las fechas son iguales, ordenar por hora numéricamente (descendente)
          const timeDescA = b.horario_inicio || b.start_time || '';
          const timeDescB = a.horario_inicio || a.start_time || '';
          
          if (timeDescA && timeDescB) {
            // Convertir hora "HH:MM" a minutos para comparación numérica
            const parseTime = (timeStr) => {
              const [hours, minutes] = timeStr.split(':').map(Number);
              return hours * 60 + minutes;
            };
            
            return parseTime(timeDescA) - parseTime(timeDescB);
          }
          
          if (timeDescA && !timeDescB) return -1;
          if (!timeDescA && timeDescB) return 1;
          
          return 0;
        case 'priority':
          const priorityOrder = { urgent: 0, high: 1, medium: 2, low: 3 };
          return priorityOrder[a.priority] - priorityOrder[b.priority];
        case 'title':
          return a.title.localeCompare(b.title);
        default:
          return 0;
      }
    });

    return sorted;
  }, [tasks, searchTerm, filterType, filterStatus, filterPriority, sortBy, showPast]);

  const stats = useMemo(() => {
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);
    
    const pending = tasks.filter(t => t.status === 'pending' || !t.completed).length;
    const completed = tasks.filter(t => t.status === 'completed' || t.completed).length;
    
    const overdue = tasks.filter(t => {
      const dateStr = t.start_datetime || t.due_date;
      if (!dateStr) return false;
      const d = new Date(dateStr);
      d.setHours(0, 0, 0, 0);
      return (t.status === 'pending' || !t.completed) && d < todayDate;
    }).length;
    
    const today = tasks.filter(t => {
      const dateStr = t.start_datetime || t.due_date;
      if (!dateStr) return false;
      return (t.status === 'pending' || !t.completed) && isToday(new Date(dateStr));
    }).length;
    
    // Contar TODAS las tareas con fecha anterior a hoy (pasadas)
    const pastCount = tasks.filter(t => {
      const dateStr = t.start_datetime || t.due_date;
      if (!dateStr) return false;
      const d = new Date(dateStr);
      d.setHours(0, 0, 0, 0);
      return d < todayDate;
    }).length;

    return { pending, completed, overdue, today, pastCount };
  }, [tasks]);

  return (
    <div className="space-y-6">
      {/* Header con estadísticas */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-400">{stats.pending}</div>
              <div className="text-sm text-slate-400 mt-1">Pendientes</div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-cyan-400">{stats.today}</div>
              <div className="text-sm text-slate-400 mt-1">Hoy</div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-red-400">{stats.overdue}</div>
              <div className="text-sm text-slate-400 mt-1">Atrasadas</div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="pt-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-green-400">{stats.completed}</div>
              <div className="text-sm text-slate-400 mt-1">Completadas</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filtros y búsqueda */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtros y Búsqueda
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Búsqueda */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Buscar tareas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-slate-900 border-slate-600 text-white"
            />
          </div>

          {/* Filtros en grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Tipo */}
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                <SelectItem value="all" className="text-white">Todos los tipos</SelectItem>
                <SelectItem value="montaje" className="text-white">🔧 Montaje</SelectItem>
                <SelectItem value="cita_profesional" className="text-white">💼 Cita Profesional</SelectItem>
                <SelectItem value="cita_personal" className="text-white">💚 Cita Personal</SelectItem>
                <SelectItem value="ensayo" className="text-white">🎵 Ensayo</SelectItem>
                <SelectItem value="tarea_general" className="text-white">✅ Tarea General</SelectItem>
              </SelectContent>
            </Select>

            {/* Estado */}
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                <SelectItem value="all" className="text-white">Todos los estados</SelectItem>
                <SelectItem value="pending" className="text-white">Pendiente</SelectItem>
                <SelectItem value="completed" className="text-white">Completada</SelectItem>
                <SelectItem value="cancelled" className="text-white">Cancelada</SelectItem>
              </SelectContent>
            </Select>

            {/* Prioridad */}
            <Select value={filterPriority} onValueChange={setFilterPriority}>
              <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                <SelectValue placeholder="Prioridad" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                <SelectItem value="all" className="text-white">Todas</SelectItem>
                <SelectItem value="urgent" className="text-white">🔴 Urgente</SelectItem>
                <SelectItem value="high" className="text-white">🟠 Alta</SelectItem>
                <SelectItem value="medium" className="text-white">🟡 Media</SelectItem>
                <SelectItem value="low" className="text-white">🟢 Baja</SelectItem>
              </SelectContent>
            </Select>

            {/* Ordenar */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="bg-slate-900 border-slate-600 text-white">
                <SelectValue placeholder="Ordenar" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-600">
                <SelectItem value="date" className="text-white">Fecha (más próxima)</SelectItem>
                <SelectItem value="date_desc" className="text-white">Fecha (más lejana)</SelectItem>
                <SelectItem value="priority" className="text-white">Prioridad</SelectItem>
                <SelectItem value="title" className="text-white">Título (A-Z)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Contador de resultados y toggle pasadas */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="text-sm text-slate-400">
              Mostrando {filteredAndSortedTasks.length} de {tasks.length} tareas
            </div>
            <Button
              size="sm"
              variant={showPast ? "default" : "outline"}
              onClick={() => setShowPast(!showPast)}
              className={showPast 
                ? "bg-amber-600 hover:bg-amber-700 text-white text-xs h-7" 
                : "border-slate-600 text-slate-400 hover:text-white text-xs h-7"
              }
              data-testid="toggle-past-tasks"
            >
              <Clock className="w-3 h-3 mr-1.5" />
              {showPast ? 'Ocultar pasadas' : `Ver pasadas (${stats.pastCount})`}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de tareas */}
      <Card className="bg-slate-800 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white">
            Todas las Tareas ({filteredAndSortedTasks.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredAndSortedTasks.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle2 className="w-16 h-16 mx-auto text-slate-600 mb-4" />
              <p className="text-slate-400">No se encontraron tareas</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredAndSortedTasks.map((task) => {
                const TaskIcon = TASK_ICONS[task.task_type] || CheckSquare;
                const taskDate = new Date(task.start_datetime);
                const isOverdue = task.status === 'pending' && isPast(taskDate) && !isToday(taskDate);
                const isTodayTask = isToday(taskDate);

                return (
                  <div
                    key={task.id}
                    className={`
                      p-4 rounded-lg border transition-all
                      ${task.status === 'completed' 
                        ? 'bg-slate-900/30 border-slate-700 opacity-60' 
                        : isOverdue
                        ? 'bg-red-900/20 border-red-600/30'
                        : isTodayTask
                        ? 'bg-cyan-900/20 border-cyan-600/30'
                        : 'bg-slate-900/50 border-slate-700'
                      }
                    `}
                  >
                    <div className="flex items-start gap-4">
                      {/* Icono */}
                      <div 
                        className="p-3 rounded-lg flex-shrink-0"
                        style={{ 
                          backgroundColor: `${TASK_COLORS[task.task_type]}20`,
                        }}
                      >
                        <TaskIcon 
                          className="w-5 h-5" 
                          style={{ color: TASK_COLORS[task.task_type] }}
                        />
                      </div>

                      {/* Contenido */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3 mb-2">
                          <h3 className={`text-lg font-semibold ${
                            task.status === 'completed' 
                              ? 'text-slate-500 line-through' 
                              : 'text-white'
                          }`}>
                            {task.title}
                          </h3>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {(PRIORITY_LABELS[task.priority] || PRIORITY_LABELS.medium).split(' ')[0]}
                            {task.status === 'completed' && (
                              <Badge className="bg-green-600 text-white">
                                ✓ Completada
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Metadata */}
                        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400 mb-3">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {format(taskDate, "dd MMM yyyy", { locale: es })}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {format(taskDate, "HH:mm", { locale: es })}
                          </span>
                          <Badge variant="outline" className="border-slate-600 text-slate-300">
                            {TASK_TYPE_LABELS[task.task_type] || task.task_type}
                          </Badge>
                          {/* Indicador de tarea recurrente */}
                          {(task.is_recurring || task.parent_task_id) && (
                            <Badge className="bg-purple-600/20 text-purple-300 border border-purple-500/30">
                              <Repeat className="w-3 h-3 mr-1" />
                              {task.recurrence_frequency 
                                ? FREQUENCY_LABELS[task.recurrence_frequency] 
                                : 'Serie'}
                            </Badge>
                          )}
                          {isOverdue && (
                            <Badge className="bg-red-600 text-white">
                              ⚠️ Atrasada
                            </Badge>
                          )}
                          {isTodayTask && task.status === 'pending' && (
                            <Badge className="bg-cyan-600 text-white">
                              📅 Hoy
                            </Badge>
                          )}
                        </div>

                        {/* Notas */}
                        {task.notes && (
                          <p className="text-sm text-slate-400 mb-3 line-clamp-2">
                            {task.notes}
                          </p>
                        )}

                        {/* Acciones */}
                        <div className="flex gap-2 flex-wrap">
                          <Button
                            size="sm"
                            onClick={() => handleEditClick(task)}
                            className="bg-cyan-600 hover:bg-cyan-700 text-white"
                          >
                            <Edit className="w-4 h-4 mr-1" />
                            Editar
                            {(task.is_recurring || task.parent_task_id) && (
                              <Repeat className="w-3 h-3 ml-1 opacity-70" />
                            )}
                          </Button>
                          {task.status === 'pending' && (
                            <Button
                              size="sm"
                              onClick={() => onCompleteTask(task.id)}
                              className="bg-green-600 hover:bg-green-700 text-white"
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              Completar
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDeleteClick(task)}
                            className="bg-red-600 hover:bg-red-700 text-white"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Eliminar
                            {(task.is_recurring || task.parent_task_id) && (
                              <Repeat className="w-3 h-3 ml-1 opacity-70" />
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Diálogo para tareas recurrentes */}
      <RecurringTaskDialog
        isOpen={recurringDialogOpen}
        onClose={() => {
          setRecurringDialogOpen(false);
          setSelectedTask(null);
        }}
        task={selectedTask}
        action={recurringDialogAction}
        onConfirm={handleRecurringConfirm}
      />
    </div>
  );
};
